import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import { shouldSuppressAutoFocus } from "@/hooks/use-mobile"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // `dialog-overlay-animated` / `dialog-animated` : ESSAI d'animation des
      // modales demandé par le PO le 2026-09-16 (fondu du fond, fenêtre qui
      // grossit légèrement depuis son centre) — `index.css`. Les classes
      // `animate-in`, `zoom-in-95`, `slide-in-from-*` sont INERTES ici :
      // tailwindcss-animate n'est pas installé.
      "dialog-overlay-animated fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  overlayClassName?: string
  /** When true, hides the X close button (useful when footer has Cancel) */
  hideCloseButton?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, overlayClassName, hideCloseButton, ...props }, ref) => {
  const { t } = useTranslation()
  return (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      // TECH-011 — Mobile/tactile : ne PAS auto-focuser le 1er élément focusable
      // à l'ouverture (sinon le clavier virtuel surgit et masque la modale).
      // Placé avant {...props} → un appelant peut toujours surcharger ce handler.
      onOpenAutoFocus={(event) => {
        if (shouldSuppressAutoFocus()) event.preventDefault()
      }}
      className={cn(
        // TECH-011 — Mobile-first : ancré sous la safe-area-top pour ne
        // jamais chevaucher la Dynamic Island / status bar. Hauteur max
        // bornée par 100dvh - safe-areas - marge, scroll interne si dépasse.
        // Desktop (sm+) : centrage classique restauré.
        // overflow-x-hidden : garde-fou systémique — un descendant `w-full` dans
        // ce conteneur `grid`/`fixed` (dont les items ont `min-width:auto`) peut
        // sinon s'échapper à droite, gonfler le layout viewport mobile et décaler
        // toute la modale hors de l'écran (textes tronqués au bord). Cf. dialogs
        // qui l'avaient déjà corrigé au cas par cas (ActivityDialog, GedAutoAttach…).
        // grid-cols-1 (= minmax(0,1fr)) : sans piste explicite, un item au contenu
        // insécable (email, référence) élargit la piste AU-DELÀ du panneau — tous
        // les items débordent alors à droite sous le clip (badges rognés, texte
        // d'en-tête filant sous le X malgré son pr-10). La piste bornée force le
        // reflux à la largeur du panneau.
        // Animation (ESSAI du 2026-09-16) : voir le commentaire de l'overlay.
        "dialog-animated fixed left-[50%] top-[calc(env(safe-area-inset-top)+1rem)] sm:top-[50%] z-50 grid grid-cols-1 w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-0 sm:translate-y-[-50%] max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-2rem)] sm:max-h-[calc(100vh-4rem)] overflow-y-auto overflow-x-hidden overscroll-contain gap-4 border bg-background p-6 shadow-lg duration-200 rounded-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      )}
      {...props}
    >
      {children}
      {/* 44 px au doigt (Apple HIG), 40 au pointeur — l'icône ne change pas de
            taille, seule la cible grandit. La croix est la SEULE sortie d'une modale
            sur un téléphone sans clavier : la sous-dimensionner, c'est rendre l'écran
            difficile à quitter.

            ⚠️ Elle reste en `absolute` : ce conteneur est lui-même la boîte qui
            défile, donc sur une modale plus haute que la fenêtre elle part avec le
            contenu. La rendre `sticky` imposerait une rangée de grille aux 245
            appelants — dette assumée et écrite, pas oubliée. Ce qui a été corrigé,
            c'est la cause du signalement PO : le moule plein écran (voir
            `LARGE_DIALOG_CLASS` plus bas), qui remontait le panneau à `y = 0` et
            plaçait donc cette croix SOUS la Dynamic Island. */}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-3 top-3 h-11 w-11 md:right-4 md:top-4 md:h-10 md:w-10 flex items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          {/* Le seul texte de la croix de fermeture, et il était en dur en anglais :
              un lecteur d'écran en français annonçait « Close » sur TOUTES les
              modales du produit. Un `sr-only` ne se voit pas — c'est précisément ce
              qui l'a laissé passer. */}
          <span className="sr-only">{t("common.close")}</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left pr-10",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

/**
 * Moule des GRANDES modales — réglages, détail riche, facture.
 *
 * ## 🔴 Ce qu'il remplace, et pourquoi
 *
 * Six écrans portaient chacun leur propre copie d'un moule « plein écran sous
 * `md:` » : `inset-0 h-full w-full max-w-none max-h-none translate-x-0
 * translate-y-0 rounded-none border-0`. L'intention de l'arbitrage du 2026-08-13
 * était bonne — une page de réglages dense mérite tout l'écran d'un téléphone —
 * mais l'exécution retirait à la modale ses TROIS sorties d'un coup :
 *
 * 1. Le panneau devenait exactement le viewport (mesuré : 430 × 932 à `x=0 y=0`,
 *    `border-width: 0`, fond opaque). Le voile `bg-black/80` était bien rendu, mais
 *    intégralement recouvert : **plus un pixel à taper pour sortir**.
 * 2. `inset-0` remontant le panneau à `y = 0`, la croix du socle — en `absolute
 *    top-4` — tombait ENTIÈREMENT dans les 44 à 59 px de la Dynamic Island en PWA
 *    installée. Le tap allait à la barre d'état.
 * 3. Échap n'existe pas sur un téléphone.
 *
 * Résultat, signalé par le PO le 2026-08-30 sur « Réglages de la couverture » :
 * *« la modale est pleine page et donc on ne peut plus fermer la page »*. Un écran
 * sans sortie est le pire défaut d'interface qu'on puisse livrer.
 *
 * ## Ce que fait ce moule
 *
 * Il ne redéfinit QUE ce qui distingue une grande modale d'une modale ordinaire :
 * elle est plus large au bureau, et sa colonne est un `flex` (ses sections
 * s'étirent). Tout le reste — gouttière latérale, cadre, coins, hauteur bornée par
 * les safe-areas, défilement interne, croix — vient du socle et n'est plus écrasé.
 *
 * ⚠️ Ne JAMAIS y remettre une largeur ou un `inset` mobile : c'est exactement le
 * geste qui a coûté ses sorties à l'écran. Une modale reste une COUCHE ; si un
 * écran a besoin de toute la place, il lui faut une ROUTE, pas une modale déguisée.
 */
export const LARGE_DIALOG_CLASS =
  "flex flex-col items-stretch p-4 md:max-w-3xl md:max-h-[85vh] md:p-6"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
